import type {
  CRMAccountResponse,
  EntityResponse,
  SalesforceAccountResponse,
} from 'types/api-responses';
import { EMPTY_CELL } from 'utils/empty-cell';

/**
 * A Salesforce billing address on one line.
 * @param billingAddress - The address object, a pre-joined string, or null
 * @return {string} The address, or the em dash
 */
export const formatBillingAddress = (
  billingAddress: SalesforceAccountResponse['billingAddress'] | string | null
): string => {
  if (!billingAddress) return EMPTY_CELL;
  if (typeof billingAddress === 'string') return billingAddress;
  const parts = [
    billingAddress.street,
    billingAddress.city,
    billingAddress.state,
    billingAddress.postalCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : EMPTY_CELL;
};

/**
 * An entity's first address on one line.
 * @param addresses - The entity's addresses
 * @return {string} The address, or the em dash
 */
export const formatAddress = (
  addresses: EntityResponse['addresses']
): string => {
  if (!addresses || addresses.length === 0) return EMPTY_CELL;
  const firstAddress = addresses[0];
  const parts = [
    firstAddress.streetAddress,
    firstAddress.city,
    firstAddress.state,
    firstAddress.zipCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : EMPTY_CELL;
};

/**
 * An entity's phone numbers, comma-separated.
 * @param phoneNumbers - The entity's phone numbers
 * @return {string} The numbers, or the em dash
 */
export const formatPhoneNumbers = (
  phoneNumbers: EntityResponse['phoneNumbers']
): string => {
  if (!phoneNumbers || phoneNumbers.length === 0) return EMPTY_CELL;
  return phoneNumbers.map((p) => p.number).join(', ');
};

/**
 * A CRM account in the Salesforce account shape the entity views render.
 * @param {CRMAccountResponse} account - The CRM account
 * @return {SalesforceAccountResponse} The account as a Salesforce account
 */
export const convertToSalesforceAccount = (
  account: CRMAccountResponse
): SalesforceAccountResponse => {
  const billingAddressObj: SalesforceAccountResponse['billingAddress'] = {
    city: null,
    country: null,
    postalCode: null,
    state: null,
    street: null,
  };

  if (account.billingAddress) {
    billingAddressObj.street = account.billingAddress;
  }

  return {
    salesforceId: account.salesforceId,
    salesforceHref: account.salesforceHref,
    name: account.name,
    type: account.type || '',
    billingAddress: billingAddressObj,
  };
};
